import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaPrint } from 'react-icons/fa';
import cloudsafeLogo from '../../../assets/cloudsafe-logo.png';
import { fetchReport } from './mockReport';
import './ComplianceReport.css';

const fmtDate = (iso, opts) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', opts || {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
};

const outcomePill = (outcome) => {
  const o = (outcome || '').toLowerCase();
  if (o === 'pass')    return <span className="rep-pill rep-pill-good">Pass</span>;
  if (o === 'fail')    return <span className="rep-pill rep-pill-bad">Fail</span>;
  if (o === 'pending') return <span className="rep-pill rep-pill-pending">Pending</span>;
  return <span className="rep-pill">{outcome || '—'}</span>;
};

const statusPill = (status) => {
  if (status === 'overdue')  return <span className="rep-pill rep-pill-bad">Overdue</span>;
  if (status === 'due-soon') return <span className="rep-pill rep-pill-due">Due Soon</span>;
  return <span className="rep-pill">{status}</span>;
};

const PageFooter = ({ page, total, report }) => (
  <div className="rep-page-footer">
    <span>
      {report.property.name} · Compliance Report · {report.period.label}
    </span>
    <span>Page {page} of {total}</span>
  </div>
);

const ComplianceReport = () => {
  const navigate = useNavigate();
  const { id, reportId } = useParams();
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setError(null);
    fetchReport(id, reportId)
      .then(setReport)
      .catch(err => setError(err.message || 'Failed to load report'));
  }, [id, reportId]);

  const totalPages = useMemo(() => {
    if (!report) return 1;
    // cover + summary + one page per module + outstanding + appendix
    return 1 + 1 + report.modules.length + 1 + 1;
  }, [report]);

  if (error) {
    return (
      <div className="rep-viewport">
        <div className="rep-toolbar">
          <button type="button" className="rep-toolbar-back" onClick={() => navigate(`/properties/${id}`)}>
            <FaArrowLeft /> Back to property
          </button>
          <span className="rep-toolbar-title">Report</span>
          <span style={{ width: 200 }} />
        </div>
        <div className="rep-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
          <strong>Report unavailable</strong>
          <span style={{ color: '#6b7280' }}>{error}</span>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="rep-viewport">
        <div className="rep-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          Loading report…
        </div>
      </div>
    );
  }

  const { property, client, period, summary, modules, outstanding, appendix, generatedAt, preparedBy } = report;

  let pageNo = 0;
  const nextPage = () => ++pageNo;

  // Cover
  const coverPage = (
    <section className="rep-page" key="cover">
      <div className="rep-cover-head">
        <img src={cloudsafeLogo} alt="CloudSafe" className="rep-cover-logo" />
        {client.logoUrl ? (
          <img src={client.logoUrl} alt={client.name} className="rep-cover-client-logo" />
        ) : (
          <div className="rep-cover-client-fallback">{client.name}</div>
        )}
      </div>

      <div className="rep-cover-body">
        <p className="rep-cover-eyebrow">Compliance Report</p>
        <h1 className="rep-cover-title">{property.name}</h1>
        <p className="rep-cover-subtitle">{client.name}</p>
        <p className="rep-cover-period">{period.label}</p>
        <p className="rep-cover-address">{property.address}</p>
      </div>

      <dl className="rep-cover-meta">
        <div>
          <dt>Reporting Period</dt>
          <dd>{fmtDate(period.start)} — {fmtDate(period.end)}</dd>
        </div>
        <div>
          <dt>Generated</dt>
          <dd>{fmtDate(generatedAt)}</dd>
        </div>
        <div>
          <dt>Prepared For</dt>
          <dd>
            {client.contact?.name}
            {client.contact?.role ? `, ${client.contact.role}` : ''}
          </dd>
        </div>
        <div>
          <dt>Prepared By</dt>
          <dd>
            {preparedBy.name}
            {preparedBy.role ? `, ${preparedBy.role}` : ''}
          </dd>
        </div>
      </dl>

      <PageFooter page={nextPage()} total={totalPages} report={report} />
    </section>
  );

  // Executive summary
  const summaryPage = (
    <section className="rep-page" key="summary">
      <h2 className="rep-h1">Executive Summary</h2>
      <p className="rep-h1-sub">
        Headline findings across {property.name} for {period.label}.
      </p>

      <div className="rep-score-row">
        <div className="rep-score-card">
          <p className="rep-score-label">Portfolio Compliance</p>
          <div className="rep-score-pct">{summary.compliancePct}%</div>
          <p className="rep-score-of">
            {summary.compliant} of {summary.totalAssets} assets compliant
          </p>
          <div className="rep-score-bar" aria-hidden="true">
            <div className="rep-score-bar-fill" style={{ width: `${summary.compliancePct}%` }} />
          </div>
        </div>

        <div className="rep-stat-stack">
          <div className="rep-stat-row">
            <span className="rep-stat-dot rep-dot-good" />
            <span className="rep-stat-label">Compliant</span>
            <span className="rep-stat-num">{summary.compliant}</span>
          </div>
          <div className="rep-stat-row">
            <span className="rep-stat-dot rep-dot-due" />
            <span className="rep-stat-label">Due Soon</span>
            <span className="rep-stat-num">{summary.dueSoon}</span>
          </div>
          <div className="rep-stat-row">
            <span className="rep-stat-dot rep-dot-bad" />
            <span className="rep-stat-label">Overdue</span>
            <span className="rep-stat-num">{summary.overdue}</span>
          </div>
        </div>
      </div>

      <hr className="rep-divider" />

      <h3 className="rep-h2">Summary</h3>
      <p className="rep-p">{summary.narrative}</p>

      <PageFooter page={nextPage()} total={totalPages} report={report} />
    </section>
  );

  // Module pages — one page per module so printed breaks are clean
  const modulePages = modules.map(mod => (
    <section className="rep-page" key={`mod-${mod.key}`}>
      <div className="rep-module-head">
        <h2 className="rep-h1">{mod.label}</h2>
        <div className="rep-module-stats">
          <span><strong>{mod.stats.total}</strong> assets</span>
          <span><strong>{mod.stats.compliant}</strong> compliant</span>
          <span><strong>{mod.stats.dueSoon}</strong> due soon</span>
          <span><strong>{mod.stats.overdue}</strong> overdue</span>
        </div>
      </div>
      <p className="rep-h1-sub">Inspections completed during {period.label}.</p>

      {mod.inspections.length === 0 ? (
        <p className="rep-p rep-muted">No inspections recorded in this period.</p>
      ) : (
        <table className="rep-table">
          <thead>
            <tr>
              <th style={{ width: '18%' }}>Date</th>
              <th style={{ width: '32%' }}>Type</th>
              <th style={{ width: '12%' }}>Outcome</th>
              <th style={{ width: '22%' }}>Contractor</th>
              <th style={{ width: '16%' }}>Evidence</th>
            </tr>
          </thead>
          <tbody>
            {mod.inspections.map((insp, i) => (
              <tr key={i}>
                <td>{fmtDate(insp.date)}</td>
                <td>{insp.type}</td>
                <td>{outcomePill(insp.outcome)}</td>
                <td>{insp.contractor}</td>
                <td>
                  {insp.evidenceUrl ? (
                    <a className="rep-link" href={insp.evidenceUrl} target="_blank" rel="noreferrer">
                      View PDF
                    </a>
                  ) : (
                    <span className="rep-muted">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <PageFooter page={nextPage()} total={totalPages} report={report} />
    </section>
  ));

  // Outstanding actions
  const prioRank = { high: 0, medium: 1, low: 2 };
  const outstandingSorted = [...outstanding].sort((a, b) => {
    if (a.status !== b.status) return a.status === 'overdue' ? -1 : 1;
    const p = (prioRank[a.priority] ?? 9) - (prioRank[b.priority] ?? 9);
    if (p !== 0) return p;
    return new Date(a.due) - new Date(b.due);
  });

  const outstandingPage = (
    <section className="rep-page" key="outstanding">
      <h2 className="rep-h1">Outstanding Actions</h2>
      <p className="rep-h1-sub">
        All items overdue or falling due within 30 days of {fmtDate(period.end)}, sorted by urgency.
      </p>

      <table className="rep-table">
        <thead>
          <tr>
            <th style={{ width: '14%' }}>Module</th>
            <th>Item</th>
            <th style={{ width: '14%' }}>Due</th>
            <th style={{ width: '12%' }}>Priority</th>
            <th style={{ width: '14%' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {outstandingSorted.map((o, i) => (
            <tr key={i}>
              <td>{o.property}</td>
              <td>{o.item}</td>
              <td>{fmtDate(o.due)}</td>
              <td><span className={`rep-prio-${o.priority}`}>{o.priority}</span></td>
              <td>{statusPill(o.status)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <PageFooter page={nextPage()} total={totalPages} report={report} />
    </section>
  );

  // Appendix
  const appendixPage = (
    <section className="rep-page" key="appendix">
      <h2 className="rep-h1">Appendix — Documents</h2>
      <p className="rep-h1-sub">Documents added or renewed during {period.label}.</p>

      {appendix.length === 0 ? (
        <p className="rep-p rep-muted">No documents changed in this period.</p>
      ) : (
        <table className="rep-table">
          <thead>
            <tr>
              <th style={{ width: '48%' }}>Title</th>
              <th style={{ width: '14%' }}>Action</th>
              <th style={{ width: '16%' }}>Date</th>
              <th>Owner</th>
            </tr>
          </thead>
          <tbody>
            {appendix.map((d, i) => (
              <tr key={i}>
                <td>{d.title}</td>
                <td>{d.type}</td>
                <td>{fmtDate(d.date)}</td>
                <td>{d.owner}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <PageFooter page={nextPage()} total={totalPages} report={report} />
    </section>
  );

  return (
    <div className="rep-viewport">
      <div className="rep-toolbar">
        <button type="button" className="rep-toolbar-back" onClick={() => navigate(`/properties/${property.id}`)}>
          <FaArrowLeft /> Back to property
        </button>
        <span className="rep-toolbar-title">
          {property.name} · {period.label}
        </span>
        <button type="button" className="rep-toolbar-print" onClick={() => window.print()}>
          <FaPrint /> Print / Download PDF
        </button>
      </div>

      {coverPage}
      {summaryPage}
      {modulePages}
      {outstandingPage}
      {appendixPage}
    </div>
  );
};

export default ComplianceReport;
